import { css } from '@emotion/react'
import { Component } from 'react'
import { FieldArray } from 'react-final-form-arrays'
import { Field } from 'react-final-form'
import dayjs from 'dayjs'

import { TAX_TYPES_TEXT } from '../../../constants'
import { CurrencyInput, DatePicker, MultiButtonInput } from '../../../components'
import { placeholderSymbolReplace, numberToDollars } from '../../../utils'
import {
  TableTitle,
  TableList,
  TableRow,
  ResponsiveLabel,
  breakpoint,
} from './UpdateInvestmentsGroupStyle'
import { calculateBalance } from './UpdateBalanceUtils'

// Column widths mirror UpdateInvestmentsGroup so the not-invested group lines up with the
// invested groups above it on the page. The first cell stands in for the invested group's
// delete-icon column, and a shares placeholder keeps the balance column aligned.
const widths = {
  lead: '42px',
  name: '320px',
  taxType: '260px',
  date: '225px',
  shares: '100px',
  balance: '145px',
}

const desktopOnly = css`
  @media (max-width: ${breakpoint}) {
    display: none;
  }
`

// Editable group for investments that are currently "not invested" (taxType 0, no balance).
// Entering a balance here converts the option into an invested position on save, so each row
// also picks a tax type. Balances are captured as account-balance dollars (no share entry).
class UpdateNotInvestedGroup extends Component {
  render() {
    const { title, balanceLabel, fieldName, investments, allowedTaxTypes, enablePurchaseDate } =
      this.props

    if (!investments || investments.length === 0) {
      return null
    }

    const showTaxTypeSelector = allowedTaxTypes.length > 1
    const singleTaxTypeText =
      allowedTaxTypes.length > 0 ? TAX_TYPES_TEXT[allowedTaxTypes[0].value] : ''

    return (
      <div>
        <TableTitle>
          <div>{title}&nbsp;&nbsp;&nbsp;</div>
          <div>
            {balanceLabel} ${numberToDollars(calculateBalance(investments))}
          </div>
        </TableTitle>

        <div
          css={css`
            display: flex;
            font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
            font-size: 14px;
            color: #7a8e96;
            line-height: 1.5;
            ${desktopOnly};
            & > div {
              padding: 6px 8px;
            }
          `}>
          <div
            css={css`
              width: ${widths.lead};
            `}
          />
          <div
            css={css`
              width: ${widths.name};
            `}>
            Investment name
          </div>
          <div
            css={css`
              width: ${widths.taxType};
            `}>
            {showTaxTypeSelector ? 'Tax type' : ''}
          </div>
          <div
            css={css`
              width: ${widths.date};
            `}>
            {enablePurchaseDate ? 'As of date' : ''}
          </div>
          <div
            css={css`
              width: ${widths.shares};
            `}
          />
          <div
            css={css`
              width: ${widths.balance};
              text-align: right;
            `}>
            Balance
          </div>
        </div>

        <FieldArray name={fieldName}>
          {({ fields }) => (
            <TableList>
              {fields.map(name => (
                <Field
                  key={name}
                  name={name}
                  render={({ input }) => {
                    const { securityName } = input.value

                    return (
                      <TableRow>
                        <div
                          css={css`
                            width: ${widths.lead};
                            ${desktopOnly};
                          `}
                        />

                        <ResponsiveLabel>Investment name</ResponsiveLabel>
                        <div
                          css={css`
                            width: ${widths.name};
                            padding: 12px 8px;
                            @media (max-width: ${breakpoint}) {
                              padding: 0 8px;
                              width: 100%;
                            }
                          `}>
                          {placeholderSymbolReplace(securityName)}
                        </div>

                        {showTaxTypeSelector && <ResponsiveLabel>Tax type</ResponsiveLabel>}
                        <div
                          css={css`
                            width: ${widths.taxType};
                            padding: 12px 8px;
                            font-size: 14px;
                            color: #4a606a;
                            @media (max-width: ${breakpoint}) {
                              width: 100%;
                              margin-left: 45px;
                            }
                          `}>
                          {showTaxTypeSelector ? (
                            <Field
                              name={`${name}.taxType`}
                              render={({ input }) => (
                                <MultiButtonInput
                                  name={input.name}
                                  value={input.value}
                                  onChange={(fieldName, value) => input.onChange(value)}
                                  onBlur={input.onBlur}
                                  buttons={allowedTaxTypes}
                                  buttonWidth='80px'
                                  margin='0'
                                />
                              )}
                            />
                          ) : (
                            <span css={desktopOnly}>{singleTaxTypeText}</span>
                          )}
                        </div>

                        {enablePurchaseDate && <ResponsiveLabel>As of date</ResponsiveLabel>}
                        <div
                          css={css`
                            width: ${widths.date};
                            padding: 12px 0px;
                            @media (max-width: ${breakpoint}) {
                              width: 100%;
                              margin-left: 45px;
                              padding: 0;
                              ${enablePurchaseDate ? '' : 'display: none;'}
                            }
                          `}>
                          {enablePurchaseDate && (
                            <Field
                              name={`${name}.purchaseDate`}
                              render={({ input, meta }) => (
                                <DatePicker
                                  date={
                                    input.value
                                      ? dayjs(input.value).format('YYYY-MM-DD')
                                      : dayjs().format('YYYY-MM-DD')
                                  }
                                  error={meta.error}
                                  showError={meta.touched}
                                  fontSize='14px'
                                  handleChange={(name, value) => input.onChange(value)}
                                  maxYear={new Date().getFullYear()}
                                  minYear={1900}
                                  name='purchaseDate'
                                  onBlur={input.onBlur}
                                />
                              )}
                            />
                          )}
                        </div>

                        <div
                          css={css`
                            width: ${widths.shares};
                            padding: 12px 8px;
                            ${desktopOnly};
                          `}
                        />

                        <ResponsiveLabel>Balance</ResponsiveLabel>
                        <div
                          css={css`
                            width: ${widths.balance};
                            padding: 12px 8px;
                            text-align: right;
                            @media (max-width: ${breakpoint}) {
                              text-align: left;
                              width: 100%;
                              margin-left: 45px;
                              padding: 0 5px;
                            }
                            & > div > div > div > input {
                              text-align: right;
                            }
                          `}>
                          <Field
                            name={`${name}.balance`}
                            format={value => (value === null ? undefined : value)}
                            parse={v => v}
                            render={({ input }) => (
                              <CurrencyInput
                                decimalScale={2}
                                fontSize='14px'
                                name='balance'
                                onBlur={input.onBlur}
                                onChange={(fieldName, value) => input.onChange(value)}
                                value={input.value}
                                width='125px'
                                css={css`
                                  padding: 0px;
                                `}
                              />
                            )}
                          />
                        </div>
                      </TableRow>
                    )
                  }}
                />
              ))}
            </TableList>
          )}
        </FieldArray>
      </div>
    )
  }
}

export default UpdateNotInvestedGroup
